/* eslint-disable no-underscore-dangle */
import { StatsigClient } from "@statsig/js-client"
import crypto from "crypto"
import { getLocalItem, setLocalItem } from "@rentspree/cookie"
import { logger } from "utils/logger"
import { STORAGE } from "../../constants/cookie"
import { parse } from "../query-parser"
import history from "../history"
import { getSubdomain } from "../subdomain"
import { ROUTE } from "../../containers/router/constants"
import { STATSIG_API, STATSIG_ENVIRONMENT, STATSIG_SDK_KEY } from "../../env"

const STORAGE_KEY = "STATSIG_LOCAL_STORAGE_STABLE_ID"
export const STATSIG_DEFAULT_USER_ID = "default-id"

class FeatureManager {
  #initialized = false

  /** @type {Promise<void>} */
  #initPromise = null

  /** @type {StatsigClient} */
  #statsigInstance = null

  constructor() {
    if (this.#initialized) return

    this.#statsigInstance = new StatsigClient(
      STATSIG_SDK_KEY,
      {
        userID: STATSIG_DEFAULT_USER_ID,
        custom: {
          clientApp: "auth-web",
          subdomain: this._getCurrentUserSubdomain()
        },
        customIDs: {
          stableID: this._getStableId()
        }
      },
      {
        environment: {
          tier: STATSIG_ENVIRONMENT
        },
        api: STATSIG_API
      }
    )
    if (this.#statsigInstance.loadingStatus !== "Ready") {
      this.#initPromise = this.#statsigInstance
        .initializeAsync()
        .catch(error => {
          logger.error(error)
        })
        .finally(() => {
          this.#initialized = true
        })
    } else {
      this.#initialized = true
      this.#initPromise = Promise.resolve()
    }
  }

  /*
    This function based on this link
    https://docs.statsig.com/client/javascript-sdk/stable-id/#persisting-stableid-across-subdomain
   */
  _getStableId = () => {
    const generateUUID = () => {
      if (crypto && crypto.randomUUID) return crypto.randomUUID()

      const randomSegment = () =>
        Math.floor(65536 * Math.random())
          .toString(16)
          .padStart(4, "0")

      return `${randomSegment()}${randomSegment()}-${randomSegment()}-4${randomSegment().substring(
        1
      )}-${randomSegment()}-${randomSegment()}${randomSegment()}${randomSegment()}`
    }

    const localStorageUUID = localStorage.getItem(STORAGE_KEY) || null
    const cookieUUID = getLocalItem(STORAGE.USER_STABLE_ID)

    if (!cookieUUID && !localStorageUUID) {
      const newUUID = generateUUID()
      localStorage.setItem(STORAGE_KEY, newUUID)
      setLocalItem(STORAGE.USER_STABLE_ID, newUUID)
      return newUUID
    }
    if (!cookieUUID && localStorageUUID) {
      setLocalItem(STORAGE.USER_STABLE_ID, localStorageUUID)
      return localStorageUUID
    }
    if (cookieUUID) {
      localStorage.setItem(STORAGE_KEY, cookieUUID)
      return cookieUUID
    }

    return cookieUUID
  }

  /*
    auth-web cannot detect user subdomain directly,
    we need to detect it from continue query string with contains the real URL with subdomain.
   */
  _getCurrentUserSubdomain = () => {
    let url = parse(history.location.search).continue || null
    const { pathname } = history.location
    // Exact paths plus the /login/* and /oauth/* sub-trees (e.g. /login/google,
    // /oauth/applicant). Prefix uses a trailing "/" so /login-and-security is
    // not treated as pre-login.
    const isPreLoginPath =
      [
        ROUTE.SIGNUP,
        ROUTE.CALLBACK,
        ROUTE.AUTHENTICATED,
        ROUTE.LOGOUT,
        ROUTE.SESSION_TIMEOUT
      ].includes(pathname) ||
      [ROUTE.LOGIN, ROUTE.OAUTH].some(
        base => pathname === base || pathname.startsWith(`${base}/`)
      )
    if (url == null && !isPreLoginPath) {
      url = window.location.href
    }
    return getSubdomain(url)
  }

  _ensureInitialized() {
    if (!this.#initialized) {
      throw new Error("featureManager not initialized. Call init() first.")
    }
  }

  getStatsigInstance() {
    return this.#statsigInstance
  }

  getStableId() {
    this._ensureInitialized()
    return this.#statsigInstance.getContext().stableID
  }

  checkGate(gateName) {
    this._ensureInitialized()
    return this.#statsigInstance.checkGate(gateName)
  }

  /**
   * Resolves once the Statsig client has finished initializing.
   * Imperative callers such as sagas must await this before evaluating.
   * React components should use useStatsigClient instead.
   * @returns {Promise<void>}
   */
  async waitForInitialization() {
    await this.#initPromise
  }

  /**
   * Pass disableExposureLog in options to read an arm without logging
   * a Statsig exposure. Exposure should fire once per experiment surface.
   * @param {string} experimentName
   * @param {{disableExposureLog?: boolean}} [options]
   */
  getExperiment(experimentName, options) {
    this._ensureInitialized()
    return this.#statsigInstance.getExperiment(experimentName, options)
  }

  /**
   * Re-identify the Statsig user with the real profile when it is still the
   * anonymous default, so gates evaluate against the actual user. No-op once
   * the user has already been identified.
   * @param {{id?: string|number, email?: string, createdAt?: string, externalIntegration?: boolean, isLinkedPartner?: boolean, isInvited?: boolean, registeredUserType?: string }} [profile] - The user's profile information
   * @returns {Promise<void>}
   */
  async identifyUser(profile) {
    const currentUser = this.#statsigInstance.getContext()?.user
    if (currentUser?.userID !== STATSIG_DEFAULT_USER_ID) {
      return
    }

    await this.#statsigInstance.updateUserAsync({
      ...currentUser,
      userID: profile?.id != null ? String(profile.id) : currentUser.userID,
      email: profile?.email,
      custom: {
        ...currentUser.custom,
        userCreatedAt: profile?.createdAt,
        externalIntegration: !!profile?.externalIntegration,
        isLinkedPartner: !!profile?.isLinkedPartner,
        isInvited: !!profile?.isInvited,
        registeredUserType: profile?.registeredUserType
      }
    })
  }

  shutdown() {
    if (this.#initialized) {
      this.#statsigInstance.shutdown()
      this.#initialized = false
    }
  }
}

// Singleton export
export const featureManager = new FeatureManager()
